import {Component} from '@angular/core';
import {AbstractWidgetDirective} from '../../../abstract-widget.directive';
import { UiWidget, WidgetConfiguration, WidgetValidator } from '@pt/pt-models';
import {ActionsSubject, Store} from '@ngrx/store';
import {QueryService} from '@pt/api-client';
import {
  WidgetRecipientChooserV0Args,
  WidgetRecipientChooserV0PreviewDto,
  WidgetRecipientChooserV0QueryMapped,
  WidgetRecipientChooserV0Value
} from '../models/widget-recipient-chooser-v0-preview.dto';
import {TranslateService} from '@ngx-translate/core';
import {WidgetDialogService} from '../../../../services/widget-dialog.service';
import {forkJoin} from 'rxjs';
import {map} from 'rxjs/operators';
import {WidgetUtils} from '../../../widget.utils';
import {NgxPermissionsService} from 'ngx-permissions';

@Component({
  selector: 'pt-widget-recipient-chooser-v0',
  templateUrl: './widget-recipient-chooser-v0.component.html',
  styleUrls: ['./widget-recipient-chooser-v0.component.scss']
})
export class WidgetRecipientChooserV0Component extends AbstractWidgetDirective<WidgetRecipientChooserV0PreviewDto> {
  public editorData: WidgetRecipientChooserV0PreviewDto;
  private currentValue: WidgetRecipientChooserV0Value[];
  public guaranteesRequired = false;
  public uniqueRecipient = true;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }


  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.editorData = null;
    this.uniqueRecipient = this.properties.uniqueRecipient;

    const widgetArgs: WidgetRecipientChooserV0Args = widget?.args;
    this.currentValue = widget?.value ? JSON.parse(JSON.stringify(widget?.value)) as WidgetRecipientChooserV0Value[] : [];
    if (this.widget.validators) {
      const findGuaranteesRequiredValidator = this.widget.validators?.find((v: WidgetValidator) => {
        return v.key === 'guaranteesRequired'
      });
      this.guaranteesRequired = !!findGuaranteesRequiredValidator;
    }
    forkJoin({
      country: this.service.resolve(widgetArgs?.queryCountries),
      recipientEntity: this.service.resolve(widgetArgs?.query).pipe(map(response =>
        WidgetUtils.mappingQueryResponse(
          response,
          widgetArgs?.query?.mapping))
      )
    }).subscribe((response: { country: string[], recipientEntity: WidgetRecipientChooserV0QueryMapped[] }) => {
        this.editorData = {
          argsHeader: widgetArgs.header,
          argsData: widgetArgs.data,
          value: this.currentValue,
          recipientEntity: response.recipientEntity,
          country: response.country
        } as WidgetRecipientChooserV0PreviewDto;

        this.valueController(widget, this.editorData);
        this.checkValidity();
        this.dispatchCompleteState(this.editorData);
      }, () => {
        this.dispatchErrorState();
      }
    );
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.currentValue;
    widget.comment = this.comment;
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  clickEditWidget() {
    this.dispatchEditState();
  }

  changeValue($event: WidgetRecipientChooserV0Value[]) {
    this.currentValue = $event;
    this.checkValidity();
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: false, supportValidation: true};
  }

  /**
   * In accordo con @greta questo widget non ha valori required. L'assenza di valori
   * è gestita con l'opzionalità del widget
   * 15 ottobre 2021
   * In accordo con @greta questo widget ha necessita di controllare il primo campo.
   * 07 maggio 2024
   * @private
   */
  private checkValidity() {
    let valid = true;
    if (this.currentValue?.length > 0) {
      this.currentValue.forEach(row => {
        if (!row.recipient || row.recipient?.length === 0) {
          valid = false;
        }
      })
    }
    if (this.guaranteesRequired) {
      this.currentValue.forEach(row => {
        if (row.extraUe && (!row.guarantees || row.guarantees?.length === 0)) {
          valid = false;
        }
      })
    }
    this.isValid$.next(valid);
  }

  private valueController(widget: UiWidget, data: WidgetRecipientChooserV0PreviewDto) {
    const noData = !data || data?.recipientEntity?.length === 0 || data?.country?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }

    const widgetValue: WidgetRecipientChooserV0Value[] = widget?.value ? widget?.value : [];
    const validRecipient = data?.recipientEntity?.map(d => d.uuid);
    const validCountry = data?.country;

    const validValue = widgetValue.filter(v => {
      return (!v.recipient || validRecipient.includes(v.recipient)) &&
        (!v.country || validCountry.includes(v.country));
    });

    if (validValue?.length !== widgetValue?.length) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }

  }
}
